import { inject } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import delay from 'delay'

import { EditorMarker, MediaFileBundle, Subclip, Tag } from 'elements-sdk'
import { LoggerFactory } from 'common/log'
import { Framerate, Time } from 'media/time'
import { ResolveRenderModalComponent, SequenceRenderParams } from 'panel/components/resolveRenderSequenceModal.component'
import { GenericFileReader } from 'uploader'

import { BaseInterface, loadAssetContentsPages } from './base'
import { NodeFileReader } from './common'

const require = window['nodeRequire']
const TAGS_COLUMN = 'Keywords'

export class ResolvePanelInterface extends BaseInterface {
  override logger = inject(LoggerFactory).create('ResolvePanelInterface')
  override systemPath = window['ResolveSystemPath']
  override native = true
  override canUploadSequence = true

  private resolve: any
  private projectmanager: any
  private project: any
  private pool: any
  private media: any
  private wi: any
  private ngbModal: NgbModal

  private isBusyRendering = false

  constructor () {
    super()
    this.wi = window['WorkflowIntegration']
    this.ngbModal = inject(NgbModal)
  }

  async init () {
    this.logger.info('Resolve init')
    this.platform = this.getPlatform()

    const maxAttempts = 10
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        this.active = this.wi.Initialize('com.syslink.elements')
        this.logger.info('Resolve initialization successful')
        break
      } catch (err) {
        this.logger.warn(`Attempt ${attempt + 1} to init resolve failed, retrying...`)
        await delay(500)
        if (attempt === maxAttempts - 1) {
          throw new Error(`Resolve initialization failed after maximum attempts: ${err}`)
        }
      }
    }

    this.resolve = this.wi.GetResolve()
    this.media = this.resolve.GetMediaStorage()
    this.projectmanager = this.resolve.GetProjectManager()
    this.project = this.getProject()
    this.pool = this.project.GetMediaPool()
    this.app = this.getApp()
    this.active = true
  }

  getFS () {
    return {
      ...window['nodeFS'],

      writeFile: async function (path, xml) {
        return new Promise((resolve, reject) => {
          window['nodeFS'].writeFile(path, xml, (err) => {
            if (err) {
              reject(err)
            } else {
              resolve(path)
            }
          })
        })
      },
    }
  }

  getApp () {
    return 'Resolve'
  }

  getPlatform () {
    return {
      darwin: 'osx',
      win32: 'win',
    }[process.platform] ?? process.platform
  }

  getSystemPath (name) {
    return window['getSystemPath'](name)
  }

  hasRestart () {
    return true
  }

  restart () {
    this.logger.info('Restarting extension')
    window['restartExtension']()
  }

  async open (item) {
    try {
      if (item.asset?.type === 'fcpxml') {
        await this.importEditorProject(item.mainfile.id)
      } else {
        const subclips = await this.querySubclipImport([item])
        await this.importAsset(item.asset, subclips)
      }
    } catch (error) {
      this.notify.error('Error while opening in resolve', error)
    }
  }

  override async importAsset (asset, subclips?: Subclip[]) {
    const response = await this.locateItem({ asset: asset.id })
    const result = await this.importFile(response.path)
    await this.importComments(asset, result)
    this.importTags(asset.tags, result)

    if (subclips?.length) {
      this.importSubclips(result, subclips)
    }
  }

  override async importBundles (bundles: MediaFileBundle[]) {
    if (!bundles.length) {
      return
    }

    const subclips = await this.querySubclipImport(bundles)
    const response = await this.locateItem({ path: bundles[0]!.mainfile.fullPath })
    await this.mountWorkspaces([response.workspace])

    for (const bundle of bundles) {
      if (bundle.asset) {
        if (bundle.asset.type === 'fcpxml') {
          await this.importEditorProject(bundle.mainfile.id)
        } else if (['video', 'audio', 'image'].includes(bundle.asset.type!)) {
          try {
            const bundleSubclips = subclips.filter(item => item.asset.id === bundle.asset!.id)
            const bundleFiles = await this.api.mediaLibrary.getAllMediaFilesForBundles({
              allMediaFilesForBundlesRequest: {
                bundles: [bundle.id],
              },
            })
            const result = await this.importBundleFiles(bundleFiles.map(file => file.fullPath), bundle.mainfile.fullPath)
            await this.importComments(bundle.asset, result)
            await this.importSubclips(result, bundleSubclips)
            this.importTags(bundle.asset.tags, result)
          } catch (error) {
            this.notify.error('Error while opening in resolve', error)
          }
        }
      }
    }
  }

  async importBundleFiles (paths: string[], mainFilePath: string) {
    const urls: string[] = []
    let mainFileUrl = ''

    this.logger.info('Importing bundle files:', paths)

    for (const path of paths) {
      const url = await this.clientSideUrl.getPathUrl(path, this.platform)
      urls.push(url)
      if (path === mainFilePath) {
        mainFileUrl = url
      }
    }

    return ['.dpx', '.exr'].find(ext => mainFileUrl.endsWith(ext))
      ? this.importImagesSequence(mainFileUrl, urls.length)
      : this.importPaths(urls)
  }

  getFolder (folder, name) {
    const subfolders = folder.GetSubFolderList()
    for (const subfolder of subfolders) {
      if (subfolder.GetName() === name) {
        return subfolder
      }
    }

    return this.pool.AddSubFolder(folder, name)
  }

  /**
  * Recursively imports bundles from the given directory structure.
  *
  * - For each directory, if it contains bundles, a corresponding folder is created in the pool.
  * - If a directory has no bundles and none of its children contain bundles, its folder is deleted.
  * - Ensures the pool's current folder is restored after traversal.
  *
  **/
  async importDirs (dirs, folder) {
    folder = folder ?? this.pool.GetCurrentFolder()
    let hasAnyBundles = false

    try {
      for (const dir of dirs) {
        const subfolder = this.getFolder(folder, dir.name)
        let hasBundles = false

        await loadAssetContentsPages(this.api, dir, async (bundles, directories) => {
          if (bundles.length) {
            this.pool.SetCurrentFolder(subfolder)
            await this.importBundles(bundles)
            hasBundles = true
          }

          if (directories.length) {
            const childHasBundles = await this.importDirs(directories, subfolder)
            hasBundles ||= childHasBundles
          }

          if (!hasBundles) {
            this.pool.DeleteFolders([subfolder])
          }
        })

        hasAnyBundles ||= hasBundles
      }
    } finally {
      this.pool.SetCurrentFolder(folder)
    }

    return hasAnyBundles
  }

  override importPaths (paths: string[]) {
    const pool = this.project.GetMediaPool()
    const items = pool.ImportMedia(paths)
    if (!items.length) {
      throw Error('Resolve could not import this file')
    }
    return items
  }

  async importPath (path: string) {
    const pool = this.project.GetMediaPool()
    if (path.endsWith('.xml')) {
      const timeline = pool.ImportTimelineFromFile(path)
      if (!timeline) {
        throw Error('Resolve could not import this file')
      }
      await this.updateTimelineMetadata(timeline)
      return timeline
    } else {
      const items = pool.ImportMedia(path)
      if (!items.length) {
        throw Error('Resolve could not import this file')
      }
      return items
    }
  }

  private addEditorMarkers (mediaItem, fps: number, markers: EditorMarker[]) {
    for (const marker of markers) {
      mediaItem.AddMarker(
        marker.start * fps,
        marker.color[0]!.toUpperCase() + marker.color.slice(1),
        marker.title,
        marker.text,
        Math.max(1, (marker.end - marker.start) * fps),
      )
    }
  }

  importImagesSequence (path: string, filesCount: number) {
    const sequenceDigitsRegex = /(\d+)(?=\.\w+$)/
    const match = path.match(sequenceDigitsRegex)
    if (!match) {
      throw Error('Invalid images sequence. No digits found in the filename.')
    }

    const pool = this.project.GetMediaPool()
    const digits = match[1]!
    const startIndex = +digits
    const endIndex = startIndex + filesCount - 1
    const items = pool.ImportMedia([
      {
        FilePath: path.replace(sequenceDigitsRegex, `%0${digits.length}d`),
        StartIndex: startIndex,
        EndIndex: endIndex,
      },
    ])
    if (!items.length) {
      throw Error('Resolve could not import this sequence')
    }
    return items
  }

  async importComments (asset, items) {
    const comments = await this.api.mediaLibrary.getAllComments({
      asset: asset.id,
      forRoot: this.currentMediaRootID,
    })

    const markers = await this.api.mediaLibrary.getAllMarkers({
      assetId: asset.id.toString(),
    })
    markers.sort((a, b) => a.tIn - b.tIn)

    for (const mediaItem of items) {
      for (const comment of comments) {
        let text = comment.text
        if (comment.drawing) {
          text = text ? `${text} [Drawing]` : '[Drawing]'
        }

        mediaItem.AddMarker(
          (comment.time ?? 0) * asset.format.framerateFloat,
          'Red',
          comment.user!.displayName,
          text,
          1.0,
        )
      }

      for (const marker of markers) {
        mediaItem.AddMarker(
          marker.tIn * asset.format.framerateFloat,
          'Blue',
          marker.title,
          marker.text,
          (marker.tOut - marker.tIn) * asset.format.framerateFloat,
        )
      }
    }
  }

  /**
   * Imports tags using the built-in "Keywords" metadata field.
   */
  importTags (tags: Tag[], items) {
    if (tags.length) {
      for (const mediaItem of items) {
        mediaItem.SetMetadata(TAGS_COLUMN, tags.map(t => `[${t.name}]`).join(','))
      }
    }
  }

  async importSubclips (items, subclips: Subclip[]) {
    console.log('Start import subclip')
    items.forEach(mediaPoolItem => {
      const fps = parseFloat(mediaPoolItem.GetClipProperty('FPS'))
      subclips.forEach(subclip => {
        const start = Math.round(subclip.tIn * fps)
        const end = Math.round(subclip.tOut * fps)
        let name = `${subclip.name}`
        const folder = this.pool.GetCurrentFolder()
        const clipNames = folder.GetClipList().map(item => item.GetName())
        if (clipNames.find(clipName => clipName.includes(name))) {
          name = `${subclip.name} - Subclip ${subclip.id}`
        }

        this.pool.CreateTimelineFromClips(name, [
          {
            mediaPoolItem,
            startFrame: start,
            endFrame: end,
          },
        ])
      })
    })
  }

  async getActiveSequenceID (): Promise<string | null> {
    const timeline = this.project.GetCurrentTimeline()
    if (!timeline) {
      return null
    }
    return timeline.uniqueId
  }

  async getActiveSequenceTimestamp (): Promise<Time | null> {
    const timeline = this.project.GetCurrentTimeline()
    if (!timeline) {
      return null
    }
    const framerate = new Framerate(timeline.GetSetting('timelineFrameRate'))
    const offset = new Time(timeline.GetStartFrame()).div(framerate)
    return Time.parse(timeline.GetCurrentTimecode(), framerate).sub(offset)
  }

  async setActiveSequenceTimestamp (t: Time): Promise<void> {
    const timeline = this.project.GetCurrentTimeline()
    if (!timeline) {
      return
    }
    if (!timeline.SetCurrentTimecode) {
      console.warn('Resolve version does not support SetCurrentTimecode')
      return
    }

    const framerate = new Framerate(timeline.GetSetting('timelineFrameRate'))
    const frame = t.toFrame(framerate)
    timeline.SetCurrentTimecode((frame + timeline.GetStartFrame()).toString())
  }

  async getActiveSequencePaths () {
    return this.getSequencePaths(this.project.GetCurrentTimeline())
  }

  private getSequencePaths (timeline) {
    if (!timeline) {
      return []
    }

    const items = new Set<string>([])

    for (const type of ['video', 'audio']) {
      for (let i = 1; i <= timeline.GetTrackCount(type); i++) {
        const trackitems = timeline.GetItemListInTrack(type, i)
        for (const item of trackitems) {
          const path = item.GetMediaPoolItem().GetClipProperty('File Path')
          items.add(path)
        }
      }
    }

    return [...items]
  }

  private getClips (folders) {
    let clips: any[] = []
    folders.forEach(folder => {
      clips = clips.concat(folder.GetClipList())
      clips = clips.concat(this.getClips(folder.GetSubFolderList()))
    })
    return clips
  }

  async getProjectPaths () {
    const pool = this.project.GetMediaPool()
    const folder = pool.GetRootFolder()
    const clips = this.getClips([folder])
    return clips.map(item => item.GetClipProperty('File Path')).filter(Boolean)
  }

  async attachProxies (pathProxyMap) {
    const pool = this.project.GetMediaPool()
    const folder = pool.GetRootFolder()
    const clips = this.getClips([folder])
      .map(item => [item.GetClipProperty('File Path'), item])
      .filter(item => item[0])

    clips.forEach(item => {
      const [path, clip] = item
      if (path in pathProxyMap) {
        clip.LinkProxyMedia(pathProxyMap[path])
      }
    })
  }

  async getLocalPath (options) {
    return window['getLocalPath'](options)
  }

  getConfig (key) {
    return window['config']?.get(key)
  }

  setConfig (key, value) {
    return window['config']?.set(key, value)
  }

  hasConfig () {
    return !!window['config']
  }

  async isProxyMode () {
    return Boolean(
      parseInt(this.project.GetSetting('perfProxyMediaMode'))
      || parseInt(this.project.GetSetting('perfProxyMediaOn')),
    )
  }

  setProxyMode (enabled) {
    this.project.SetSetting('perfProxyMediaOn', (enabled ? 1 : 0).toString())
    this.project.SetSetting('perfProxyMediaMode', (enabled ? 1 : 0).toString())
  }

  getProject () {
    return this.projectmanager.GetCurrentProject()
  }

  private getClipByPath (path: string) {
    const clips = this.getClips([this.pool.GetRootFolder()])
    return clips.find(c => c.GetClipProperty('File Path') === path)
  }

  override async getMarkersForPath (path: string): Promise<EditorMarker[]> {
    const clip = this.getClipByPath(path)
    if (!clip) {
      return []
    }

    const fps = parseFloat(clip.GetClipProperty('FPS'))
    const results: EditorMarker[] = []
    for (const [frame, marker] of Object.entries(clip.GetMarkers()) as any[]) {
      const time = parseFloat(frame) / fps
      results.push({
        start: time,
        end: time,
        title: marker.name,
        text: marker.note,
        color: marker.color.toLowerCase(),
      })
    }
    return results
  }

  override async replaceMetadataForPath (path: string, markers: EditorMarker[], newTags: Tag[]): Promise<void> {
    const clip = this.getClipByPath(path)
    if (!clip) {
      return
    }

    for (const frame of Object.keys(clip.GetMarkers())) {
      clip.DeleteMarkerAtFrame(parseInt(frame))
    }

    const fps = parseFloat(clip.GetClipProperty('FPS'))
    this.addEditorMarkers(clip, fps, markers)

    const existingTags = clip.GetMetadata(TAGS_COLUMN).split(',')
    const updatedTags = [...existingTags, ...newTags.map(t => `[${t.name}]`).filter(t => !existingTags.includes(t))]
    clip.SetMetadata(TAGS_COLUMN, updatedTags.join(','))
  }

  /**
   * When importing a timeline from an .xml file using the Resolve API, markers information
   * is not set by default and is missing in the imported timeline.
   * "Keywords" metadata which represents tags, also cannot be set during .xml import.
   *
   * This method retrieves all marker data from the source clips and updates the marker
   * information in DaVinci Resolve for both the source clips and the timeline itself.
   *
   * It retrieves markers from the timeline clips and associates them with the corresponding
   * source clips. Then, adds the markers to the respective timeline tracks.
   *
   * In addition, it retrieves tags from source clips and updates each clip from the timeline with the tag info.
   *
   * @param {Object} timeline - The timeline object to update markers for.
   */
  private async updateTimelineMetadata (timeline) {
    const paths = this.getSequencePaths(timeline)
    const response = await this.api.private.locateMetadata({
      locateMetadataEndpointRequest: {
        paths,
      },
    })

    const timelineTracks = new Map<string, any[]>()

    for (const type of ['video', 'audio']) {
      for (let i = 1; i <= timeline.GetTrackCount(type); i++) {
        const trackitems = timeline.GetItemListInTrack(type, i)
        for (const trackItem of trackitems) {
          const trackName = trackItem.GetName()
          if (!timelineTracks.has(trackName)) {
            timelineTracks.set(trackName, [])
          }
          timelineTracks.get(trackName)?.push(trackItem)
        }
      }
    }

    for (const item of response) {
      const clip = this.getClipByPath(item.path)
      const fps = parseFloat(clip.GetClipProperty('FPS'))
      this.addEditorMarkers(clip, fps, item.markers)
      this.importTags(item.tags, [clip])
      const timelineTrackItems = timelineTracks.get(clip.GetName())
      if (timelineTrackItems) {
        for (const timelineTrackItem of timelineTrackItems) {
          this.addEditorMarkers(timelineTrackItem, fps, item.markers)
        }
      }
    }
  }

  override async renderSequence (
    ready: (_: GenericFileReader) => Promise<void>,
  ): Promise<void> {
    const fs = require('fs')
    const os = require('os')
    const path = require('path')
    const presetBlacklist = [
      'Frame.io',
      'Blackmagic Cloud',
      'HyperDeck',
      'Premiere XML',
      'Avid AAF',
      'Pro Tools',
      'Audio Only',
    ]

    const modal = this.ngbModal.open(ResolveRenderModalComponent)
    modal.componentInstance.presets = this.getRenderPresets().filter(x => !presetBlacklist.includes(x))
    const params: SequenceRenderParams | null = await modal.result.catch(() => null)

    if (!params) {
      return
    }

    const process = this.notify.process(this.translate.instant('Rendering sequence'))
    try {
      this.isBusyRendering = true
      this.project.LoadRenderPreset(params.presetName)
      const extension = this.project.GetCurrentRenderFormatAndCodec().format
      const outputDir = os.tmpdir()
      const outputBasename = 'elements-sequence-scratch.'
      const outputFilename = outputBasename + extension
      const outputPath = path.join(outputDir, outputFilename)

      this.project.SetRenderSettings({
        TargetDir: outputDir,
        CustomName: outputFilename,
      })
      const uuid = this.project.AddRenderJob()
      this.project.StartRendering([uuid], true)
      while (this.project.IsRenderingInProgress()) {
        await delay(1000)
      }

      this.notify.finishProcess(process, 'success', this.translate.instant('Done'))

      const reader = new NodeFileReader(fs, outputPath, params.name + '.' + extension)
      try {
        await ready(reader)
      } finally {
        reader.close()
        fs.unlinkSync(outputPath)
      }
    } catch (e) {
      let error = e
      if (error.toString().includes('Project has no timeline')) {
        error = new Error('No active timeline')
      }
      this.notify.finishProcess(process, 'error')
      this.notify.errorFromResponse(this.translate.instant('Failed'), error)
    } finally {
      this.isBusyRendering = false
    }
  }

  getRenderPresets (): string[] {
    return this.project.GetRenderPresetList()
  }

  override isRenderingSequence (): boolean {
    return this.isBusyRendering
  }

  override openURL (url: string) {
    const { openExternal } = window['nodeRequire']('electron').shell
    openExternal(url)
  }
}
